/**
 * Intenções de escrita dos plugins web e a tradução delas em operações do NDB.
 *
 * O plugin pensa em caminhos (`doc.set('atributos.forca', 14)`); o NDB pensa em nós com id. A
 * tradução acontece no RPG Desk, contra a réplica mais nova que ele tem (e no modo de teste, contra
 * a cópia do próprio SDK): se o plugin traduzisse sozinho, a cópia dele, um pouco atrasada,
 * criaria dois nós `atributos` quando outra pessoa acabou de criar o primeiro.
 *
 * Caminhos chegam como listas de nomes; `#<id>` é o filho com aquele id (um item de lista).
 * Tudo o que vem do plugin é conferido aqui: o iframe não é confiável.
 */
import { NodeTree, attrKey, foldName, type AttrValue, type NodeId, type Op } from '@rpgdesk/ndb';

/** Grupos do NDB da mesa (os mesmos de `NDB_GROUPS`, em packages/shared/src/library.ts). */
export const GROUP_EVERYONE = 'everyone';
export const GROUP_GMS = 'gms';

/** Nome dos nós de item de lista. */
export const LIST_ITEM = 'item';

export const MAX_TEXT = 100_000;
const FIELD = /^[A-Za-z_][A-Za-z0-9_]*$/;
const ITEM_ID = /^[A-Za-z0-9_.:-]{1,80}$/;

export type Path = readonly string[];
export type PrivateLevel = 'gm' | 'owner';

export type Intent =
  /** escalar grava o atributo; objeto grava campo a campo; `null` apaga */
  | { k: 'set'; path: Path; value: unknown }
  /** novo item de lista (o id vem do SDK, para `add` devolver na hora) */
  | { k: 'add'; path: Path; id: string; value?: unknown; index?: number }
  /** apaga o campo (atributo e grupo com esse nome) ou o item `#id` */
  | { k: 'remove'; path: Path }
  /** reordena o item `#id` da lista */
  | { k: 'move'; path: Path; index: number }
  /** parte só do mestre, só do dono e dos mestres, ou de volta para todos */
  | { k: 'restrict'; path: Path; who: PrivateLevel | null };

/** Os motivos de recusa de uma intenção, nas duas línguas (o plugin recebe na da mesa). */
const INTENT_TEXT = {
  pt: {
    field: (name: string) => `"${name}" não é um nome de campo válido`,
    reserved: (name: string) => `"${name}" é reservado para o RPG Desk`,
    path: () => 'caminho inválido',
    item: (seg: string) => `"${seg}" não é um item válido`,
    finite: () => 'números precisam ser finitos',
    text: (max: number) => `o texto passa de ${max} caracteres`,
    gone: () => 'o item não existe mais (alguém pode tê-lo apagado)',
    group: (name: string) => `"${name}" é um grupo de campos; para trocar por um valor, apague antes com set(caminho, null)`,
    list: (name: string) => `"${name}": listas não se gravam de uma vez; use doc.list("${name}").add(...)`,
    value: (name: string) => `"${name}": só se gravam textos, números, verdadeiro/falso e objetos com eles`,
    itemObject: () => 'um item de lista recebe um objeto com os campos dele',
    itemId: () => 'id de item inválido',
    itemExists: () => 'já existe um item com esse id',
    index: () => 'posição inválida',
    move: () => 'move pede um item de lista',
    restrict: () => 'restrict aceita "gm", "owner" ou null',
    noOwner: () => 'este documento não tem dono',
    unknown: () => 'intenção desconhecida',
  },
  en: {
    field: (name: string) => `"${name}" is not a valid field name`,
    reserved: (name: string) => `"${name}" is reserved for RPG Desk`,
    path: () => 'invalid path',
    item: (seg: string) => `"${seg}" is not a valid item`,
    finite: () => 'numbers must be finite',
    text: (max: number) => `the text is longer than ${max} characters`,
    gone: () => 'the item no longer exists (someone may have deleted it)',
    group: (name: string) => `"${name}" is a group of fields; to replace it with a value, delete it first with set(path, null)`,
    list: (name: string) => `"${name}": lists are not saved at once; use doc.list("${name}").add(...)`,
    value: (name: string) => `"${name}": only texts, numbers, true/false and objects with them can be saved`,
    itemObject: () => 'a list item takes an object with its fields',
    itemId: () => 'invalid item id',
    itemExists: () => 'an item with this id already exists',
    index: () => 'invalid position',
    move: () => 'move takes a list item',
    restrict: () => 'restrict takes "gm", "owner" or null',
    noOwner: () => 'this document has no owner',
    unknown: () => 'unknown intent',
  },
};
type IntentKey = keyof (typeof INTENT_TEXT)['pt'];

/** Intenção recusada. A mensagem sai em português; `localized('en')` dá a mesma em inglês. */
export class IntentError extends Error {
  private readonly arg: string | number | undefined;
  constructor(
    readonly key: IntentKey,
    arg?: string | number,
  ) {
    super((INTENT_TEXT.pt[key] as (a?: string | number) => string)(arg));
    this.arg = arg;
  }

  /** A mesma mensagem na língua pedida. */
  localized(lang: 'pt' | 'en'): string {
    return (INTENT_TEXT[lang][this.key] as (a?: string | number) => string)(this.arg);
  }
}

export const isItemRef = (segment: string): boolean => segment.startsWith('#');

/** Confere um nome de campo vindo do plugin. */
export function checkName(name: unknown): string {
  if (typeof name !== 'string' || !FIELD.test(name) || name.length > 64) throw new IntentError('field', String(name));
  if (name.toLowerCase().startsWith('_rpgdesk')) throw new IntentError('reserved', name);
  return name;
}

function checkPath(path: unknown): Path {
  if (!Array.isArray(path) || path.length === 0 || path.length > 24) throw new IntentError('path');
  for (const seg of path) {
    if (typeof seg !== 'string') throw new IntentError('path');
    if (isItemRef(seg)) {
      if (!ITEM_ID.test(seg.slice(1))) throw new IntentError('item', seg);
    } else checkName(seg);
  }
  return path as Path;
}

function scalar(value: unknown): AttrValue | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new IntentError('finite');
    return value;
  }
  if (typeof value === 'string') {
    if (value.length > MAX_TEXT) throw new IntentError('text', MAX_TEXT);
    return value;
  }
  return undefined;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v) && Object.getPrototypeOf(v) === Object.prototype;

export type ResolveOptions = {
  /** id novo para um nó criado no caminho */
  newId: () => NodeId;
  /** dono do documento (a ficha), para `restrict(…, 'owner')` */
  ownerId?: string | null;
};

/**
 * Traduz as intenções em operações do NDB, na ordem, como um lote só. Não confere permissões
 * (isso é do NDB, no RPG Desk e no servidor); confere a forma do que veio.
 */
export function resolveIntents(tree: NodeTree, intents: readonly Intent[], opts: ResolveOptions): Op[] {
  const ops: Op[] = [];
  // cópia de trabalho: as intenções seguintes enxergam os nós criados pelas anteriores
  let work: NodeTree | null = null;
  const view = (): NodeTree => work ?? tree;
  const several = intents.length > 1;
  const emit = (op: Op): void => {
    ops.push(op);
    if (!work && (several || op.t !== 'set')) work = tree.clone();
    if (work) work.apply(op);
  };

  /** Id do nó no caminho, criando o que falta (ou null, sem criar). */
  const nodeAt = (path: Path, create: boolean): NodeId | null => {
    let cur = view().root.id;
    for (const seg of path) {
      if (isItemRef(seg)) {
        const id = seg.slice(1);
        const n = view().get(id);
        if (!n || n.parent?.id !== cur) {
          // apagar o que já sumiu não é erro; escrever nele é
          if (!create) return null;
          throw new IntentError('gone');
        }
        cur = id;
        continue;
      }
      const child = view().findChild(cur, seg);
      if (child) {
        cur = child.id;
        continue;
      }
      if (!create) return null;
      const id = opts.newId();
      emit({ t: 'add', parent: cur, id, name: seg });
      cur = id;
    }
    return cur;
  };

  /** Grava `value` no campo `name` do nó `node`. */
  const setField = (node: NodeId, name: string, value: unknown): void => {
    const n = view().get(node)!;
    if (value === null || value === undefined) {
      const key = attrKey(n, name);
      if (n.attrs.has(key)) emit({ t: 'set', node, name, value: null });
      if (view().findChild(node, name)) emit({ t: 'rmChildren', node, name });
      return;
    }
    const v = scalar(value);
    if (v !== undefined) {
      if (view().findChild(node, name)) throw new IntentError('group', name);
      emit({ t: 'set', node, name, value: v });
      return;
    }
    if (Array.isArray(value)) throw new IntentError('list', name);
    if (!isPlainObject(value)) throw new IntentError('value', name);
    // um objeto vira um grupo de campos; um valor com o mesmo nome dá lugar a ele
    const key = attrKey(n, name);
    if (n.attrs.has(key)) emit({ t: 'set', node, name, value: null });
    const child = view().findChild(node, name)?.id ?? null;
    let target = child;
    if (!target) {
      target = opts.newId();
      emit({ t: 'add', parent: node, id: target, name });
    }
    merge(target, value);
  };

  const merge = (node: NodeId, value: Record<string, unknown>): void => {
    for (const [name, v] of Object.entries(value)) setField(node, checkName(name), v);
  };

  for (const raw of intents) {
    const intent = raw as Intent;
    switch (intent.k) {
      case 'set': {
        const path = checkPath(intent.path);
        const last = path[path.length - 1]!;
        if (isItemRef(last)) {
          const node = nodeAt(path, false);
          if (node === null) {
            if (intent.value === null || intent.value === undefined) break;
            throw new IntentError('gone');
          }
          if (intent.value === null || intent.value === undefined) emit({ t: 'del', node });
          else if (isPlainObject(intent.value)) merge(node, intent.value);
          else throw new IntentError('itemObject');
          break;
        }
        const parentPath = path.slice(0, -1);
        const removing = intent.value === null || intent.value === undefined;
        const parent = nodeAt(parentPath, !removing);
        if (parent !== null) setField(parent, last, intent.value);
        break;
      }
      case 'add': {
        const path = checkPath(intent.path);
        if (typeof intent.id !== 'string' || !ITEM_ID.test(intent.id)) throw new IntentError('itemId');
        if (view().has(intent.id)) throw new IntentError('itemExists');
        const list = nodeAt(path, true)!;
        const index = intent.index;
        if (index !== undefined && (!Number.isInteger(index) || index < 0)) throw new IntentError('index');
        emit({ t: 'add', parent: list, id: intent.id, name: LIST_ITEM, ...(index !== undefined ? { index } : {}) });
        if (intent.value !== undefined && intent.value !== null) {
          if (!isPlainObject(intent.value)) throw new IntentError('itemObject');
          merge(intent.id, intent.value);
        }
        break;
      }
      case 'remove': {
        const path = checkPath(intent.path);
        const last = path[path.length - 1]!;
        if (isItemRef(last)) {
          const parent = nodeAt(path.slice(0, -1), false);
          const n = view().get(last.slice(1));
          if (parent !== null && n && n.parent?.id === parent) emit({ t: 'del', node: n.id });
          break;
        }
        const parent = nodeAt(path.slice(0, -1), false);
        if (parent !== null) setField(parent, last, null);
        break;
      }
      case 'move': {
        const path = checkPath(intent.path);
        if (!isItemRef(path[path.length - 1]!)) throw new IntentError('move');
        if (!Number.isInteger(intent.index) || intent.index < 0) throw new IntentError('index');
        const node = nodeAt(path, false);
        if (node !== null) emit({ t: 'move', node, index: intent.index });
        break;
      }
      case 'restrict': {
        const path = checkPath(intent.path);
        if (intent.who !== null && intent.who !== 'gm' && intent.who !== 'owner') throw new IntentError('restrict');
        if (intent.who === 'owner' && !opts.ownerId) throw new IntentError('noOwner');
        const node = nodeAt(path, intent.who !== null);
        if (node === null) break;
        for (const op of restrictOps(node, intent.who, opts.ownerId ?? null)) emit(op);
        break;
      }
      default:
        throw new IntentError('unknown');
    }
  }
  return ops;
}

/**
 * Permissões de uma parte só do mestre (`gm`) ou só do dono e dos mestres (`owner`); `null`
 * devolve a parte a todos. O servidor usa as mesmas para o `private` do manifesto.
 *
 * Só a leitura não bastaria: quem não lê também não deve escrever às cegas (no `gm`, o dono da
 * ficha escreveria por cima das notas do mestre). Os mestres passam por cima de tudo (são donos
 * do banco), e o `strongAllow` vale contra o `deny` do grupo de todos.
 */
export function restrictOps(node: NodeId, who: PrivateLevel | null, ownerId: string | null): Op[] {
  const ops: Op[] = [];
  const entry = (kind: 'user' | 'group', id: string, permission: string, allowance: 'deny' | 'strongAllow' | null): void => {
    ops.push({ t: 'perm', node, kind, id, permission, allowance });
  };
  const everyoneDenied = who === null ? null : 'deny';
  entry('group', GROUP_EVERYONE, 'read', everyoneDenied);
  entry('group', GROUP_GMS, 'read', who === null ? null : 'strongAllow');
  for (const permission of ['write', 'createChild', 'delete']) entry('group', GROUP_EVERYONE, permission, who === 'gm' ? 'deny' : null);
  if (ownerId) entry('user', ownerId, 'read', who === 'owner' ? 'strongAllow' : null);
  return ops;
}

/**
 * Um lote de quem não é mestre que cria um nó no caminho de uma parte privada. Quem não lê a parte
 * não a enxerga, e escrever nela criaria um segundo nó com o mesmo nome, visível a todos; o
 * servidor (e o modo de teste) recusam. Devolve o motivo, ou null.
 */
export function privateViolation(tree: NodeTree, ops: readonly Op[], parts: Readonly<Record<string, PrivateLevel>>, lang: 'pt' | 'en' = 'pt'): string | null {
  const fold = (path: string) => path.split('.').map(foldName).join('.');
  const privatePaths = new Set(Object.keys(parts).map(fold));
  if (privatePaths.size === 0) return null;
  const created = new Map<NodeId, string>();
  const pathOf = (id: NodeId): string | null => {
    const known = created.get(id);
    if (known !== undefined) return known;
    if (!tree.has(id)) return null;
    return tree
      .ancestry(id)
      .reverse()
      .slice(1)
      .map((n) => foldName(n.name))
      .join('.');
  };
  for (const op of ops) {
    if (op.t !== 'add') continue;
    const parent = pathOf(op.parent);
    if (parent === null) continue;
    const path = parent ? `${parent}.${foldName(op.name)}` : foldName(op.name);
    created.set(op.id, path);
    if (privatePaths.has(path)) return lang === 'en' ? `permission denied: "${path}" is a private part of the sheet` : `permission denied: "${path}" é uma parte privada da ficha`;
  }
  return null;
}

/** O caminho cai numa parte privada (ou dentro dela)? Devolve a parte, ou null. */
export function privatePartOf(path: Path, parts: Readonly<Record<string, PrivateLevel>>): string | null {
  const folded = path.map((s) => (isItemRef(s) ? s : foldName(s)));
  for (const part of Object.keys(parts)) {
    const segs = part.split('.').map(foldName);
    if (segs.length <= folded.length && segs.every((s, i) => folded[i] === s)) return part;
  }
  return null;
}
